import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  FileText, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronRight,
  Sparkles, BookOpen, Zap, AlertTriangle, RefreshCw,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { proposeFromExtraction } from '../../utils/proposeFromExtraction';
import { commitGraphOperation, APPROVE_ORDER } from '../../utils/commitGraphOperation';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import IntakeFactsV2Section from './IntakeFactsV2Section';

/**
 * Intake-2 (ADR-013) — IntakeReviewQueue.
 *
 * Lista todas as proposed_graph_operations (review_status='pending') e
 * proposed_knowledge_atoms (review_status='proposed') agrupadas por intake_run.
 * Humanos aprovam/rejeitam item-a-item ou em batch.
 *
 * Notas de design:
 *  - Atoms aprovados são commitados em knowledge_atoms imediatamente.
 *  - Graph operations aprovadas só viram review_status='approved' (commit
 *    real fica para Intake-3, quando houver commit engine).
 *  - Auto-approve elegível = atom risk_level='low' AND confidence>=0.95.
 */

const fmtPct = (v) => (v == null ? '—' : `${Math.round(Number(v) * 100)}%`);

const ConfidenceBar = ({ value }) => {
  const pct = Math.max(0, Math.min(1, Number(value) || 0));
  const tone =
    pct >= 0.9 ? 'bg-emerald-500'
      : pct >= 0.75 ? 'bg-amber-500'
        : 'bg-red-500';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-14 h-1.5 bg-slate-200 rounded overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct * 100}%` }} />
      </div>
      <span className="text-[10px] tabular-nums text-slate-600">{fmtPct(pct)}</span>
    </div>
  );
};

const RISK_TONE = {
  low:    'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  high:   'bg-red-100 text-red-800',
};
const RiskBadge = ({ level }) => {
  if (!level) return null;
  return (
    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${RISK_TONE[level] || 'bg-slate-100 text-slate-700'}`}>
      {level}
    </span>
  );
};

const SourceExcerpt = ({ excerpt, page }) => {
  const [expanded, setExpanded] = useState(false);
  if (!excerpt) return null;
  const trunc = excerpt.length > 160 ? `${excerpt.slice(0, 160)}…` : excerpt;
  return (
    <div className="text-[11px] text-slate-600 italic flex items-start gap-1 mt-1">
      <BookOpen size={11} className="text-slate-400 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <span>"{expanded || excerpt.length <= 160 ? excerpt : trunc}"</span>
        {page && <span className="text-[10px] text-slate-400 ml-1">(p. {page})</span>}
        {excerpt.length > 160 && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="ml-1 text-indigo-600 hover:text-indigo-800 not-italic"
          >
            {expanded ? 'menos' : 'mais'}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Resume operation.proposed_data em texto curto. Heurístico — só cobre os
 * tipos que o agente atual emite. Fallback mostra o JSON raw.
 */
const summarizeOperation = (op) => {
  const data = op.proposed_data || {};
  const t = op.target_table;
  if (op.operation === 'create_proposal') {
    return `Criar proposta: ${data.nome || data.titulo || '(sem nome)'}`;
  }
  if (op.operation === 'create_entity') {
    if (t === 'empresas') return `Criar empresa: ${data.empresa || data.company_name || '(sem nome)'}`;
    if (t === 'contatos') return `Criar contato: ${[data.first_name, data.last_name].filter(Boolean).join(' ') || '(sem nome)'}`;
    if (t === 'imoveis') return `Criar imóvel: ${data.nome || data.matricula || '(sem nome)'}`;
    return `Criar ${t}: ${JSON.stringify(data).slice(0, 80)}`;
  }
  if (op.operation === 'update_entity') {
    const fields = Object.entries(data).slice(0, 3)
      .map(([k, v]) => `${k}: ${typeof v === 'string' ? v.slice(0, 40) : JSON.stringify(v).slice(0, 40)}`)
      .join(', ');
    return `Atualizar ${t}: ${fields}`;
  }
  if (op.operation === 'create_edge') {
    if (t === 'imovel_proprietarios') {
      const pct = data.percentual_participacao ?? data.percentual ?? null;
      return `Vincular proprietário${pct != null ? ` (${pct}%)` : ''}`;
    }
    if (t === 'socios') {
      const pct = data.percentual_participacao ?? null;
      return `Vincular sócio${pct != null ? ` (${pct}%)` : ''}`;
    }
    if (t === 'proposta_imoveis') return 'Vincular proposta ↔ imóvel';
    return `Criar vínculo em ${t}`;
  }
  if (op.operation === 'update_edge') return `Atualizar vínculo em ${t}`;
  if (op.operation === 'link_document') return `Vincular documento em ${t}`;
  return `${op.operation} → ${t}`;
};

const MatchInfo = ({ op }) => {
  const candidates = Array.isArray(op.match_candidates) ? op.match_candidates : [];
  const top = candidates[0];
  if (!top && !op.match_strategy) return null;
  return (
    <div className="text-[10px] text-slate-500 mt-0.5">
      Match: {op.match_strategy || '—'}
      {top?.score != null && ` · score ${Number(top.score).toFixed(2)}`}
      {top?.matched_field && ` · campo ${top.matched_field}`}
      {top?.label && ` → ${top.label}`}
    </div>
  );
};

/* ── Linhas individuais (operação ou átomo) ─────────────────── */

const ContextStatusBadge = ({ op }) => {
  const status = op?.proposed_data?._context_status;
  const missing = Array.isArray(op?.proposed_data?._missing_context)
    ? op.proposed_data._missing_context : [];
  if (!status || status === 'resolved') return null;
  if (status === 'unresolved') {
    const what = missing.length > 0 ? missing.join('/') : 'empresa/oferta';
    return (
      <span className="text-[10px] bg-red-100 text-red-700 rounded px-1.5 py-0.5 font-medium">
        ⚠️ Sem {what} vinculada
      </span>
    );
  }
  if (status === 'needs_review') {
    return (
      <span className="text-[10px] bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 font-medium">
        ⚠️ Match fraco — verificar
      </span>
    );
  }
  return null;
};

const OperationRow = ({ op, onApprove, onReject, busy }) => (
  <div className="px-3 py-2 border-b border-slate-100 last:border-b-0">
    <div className="flex items-start gap-2">
      <Sparkles size={12} className="text-indigo-500 flex-shrink-0 mt-0.5" />
      <div className="flex-grow min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{op.operation}</span>
          <span className="text-[10px] text-slate-400">→ {op.target_table}</span>
          <ContextStatusBadge op={op} />
          <span className="ml-auto"><ConfidenceBar value={op.confidence} /></span>
        </div>
        <div className="text-xs text-slate-800 mt-0.5">{summarizeOperation(op)}</div>
        <MatchInfo op={op} />
        <SourceExcerpt excerpt={op.source_excerpt} page={op.source_page} />
      </div>
      <div className="flex flex-col gap-1 flex-shrink-0">
        <button
          type="button"
          disabled={busy}
          onClick={() => onApprove(op)}
          className="px-2 py-0.5 text-[10px] bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
        >
          <CheckCircle2 size={11} /> Aprovar
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onReject(op)}
          className="px-2 py-0.5 text-[10px] bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50 flex items-center gap-1"
        >
          <XCircle size={11} /> Rejeitar
        </button>
      </div>
    </div>
  </div>
);

const isAtomAutoEligible = (atom) =>
  atom.risk_level === 'low' && Number(atom.confidence) >= 0.95;

const AtomRow = ({ atom, onApprove, onReject, busy }) => {
  const eligible = isAtomAutoEligible(atom);
  const high = atom.risk_level === 'high';
  return (
    <div className="px-3 py-2 border-b border-slate-100 last:border-b-0">
      <div className="flex items-start gap-2">
        <span className="text-base leading-none mt-0.5">
          {atom.risk_level === 'high' ? '🔴' : atom.risk_level === 'medium' ? '🟡' : '🟢'}
        </span>
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {atom.category}
            </span>
            {atom.subcategory && (
              <span className="text-[10px] text-slate-400">· {atom.subcategory}</span>
            )}
            <RiskBadge level={atom.risk_level} />
            <span className="ml-auto"><ConfidenceBar value={atom.confidence} /></span>
          </div>
          <div className="text-xs text-slate-800 mt-0.5 line-clamp-2" title={atom.content || ''}>
            {atom.content}
          </div>
          {eligible && (
            <div className="text-[10px] text-emerald-700 flex items-center gap-1 mt-0.5">
              <Zap size={10} /> Auto-approve elegível
            </div>
          )}
          {high && (
            <div className="text-[10px] text-red-700 flex items-center gap-1 mt-0.5">
              <AlertTriangle size={10} /> Revisão obrigatória ({atom.subcategory || atom.category})
            </div>
          )}
          <SourceExcerpt excerpt={atom.source_excerpt} page={atom.source_page} />
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            type="button"
            disabled={busy}
            onClick={() => onApprove(atom)}
            className="px-2 py-0.5 text-[10px] bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
          >
            <CheckCircle2 size={11} /> Aprovar
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onReject(atom)}
            className="px-2 py-0.5 text-[10px] bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50 flex items-center gap-1"
          >
            <XCircle size={11} /> Rejeitar
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Card de um intake_run ──────────────────────────────────── */

const RunCard = ({ run, ops, atoms, defaultExpanded, onAfter, onCommitFeedback }) => {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const eligibleAtoms = useMemo(() => atoms.filter(isAtomAutoEligible), [atoms]);

  const approveAtomCommit = useCallback(async (atom, autoReason = null) => {
    // 1) Insert em knowledge_atoms
    const insertPayload = {
      atom_type: atom.atom_type || 'fact',
      entity_type: atom.entity_type,
      entity_id: atom.entity_id || null,
      category: atom.category,
      subcategory: atom.subcategory || null,
      content: atom.content,
      confidence: atom.confidence,
      knowledge_class: atom.knowledge_class || null,
      source_type: atom.source_type || 'document',
      source_ref: atom.intake_run_id,
      status: 'active',
      verified_by: user?.email || 'unknown',
      verified_at: new Date().toISOString(),
    };
    const { data: newAtom, error: insErr } = await supabase
      .from('knowledge_atoms')
      .insert(insertPayload)
      .select('id')
      .single();
    if (insErr) throw insErr;

    // 2) Update proposed
    const { error: upErr } = await supabase
      .from('proposed_knowledge_atoms')
      .update({
        review_status: 'approved',
        reviewed_by: user?.email || 'unknown',
        reviewed_at: new Date().toISOString(),
        applied_atom_id: newAtom.id,
        auto_approval_reason: autoReason,
      })
      .eq('id', atom.id);
    if (upErr) throw upErr;
  }, [user]);

  const handleApproveAtom = useCallback(async (atom) => {
    setBusy(true); setError(null);
    try {
      await approveAtomCommit(atom, null);
      onAfter();
    } catch (err) {
      setError(`Aprovar átomo falhou: ${err.message || err}`);
    } finally { setBusy(false); }
  }, [approveAtomCommit, onAfter]);

  const handleRejectAtom = useCallback(async (atom) => {
    const reason = window.prompt('Motivo da rejeição (opcional):');
    if (reason === null) return;
    setBusy(true); setError(null);
    try {
      const { error: err } = await supabase
        .from('proposed_knowledge_atoms')
        .update({
          review_status: 'rejected',
          reviewed_by: user?.email || 'unknown',
          reviewed_at: new Date().toISOString(),
          rejection_reason: reason || null,
        })
        .eq('id', atom.id);
      if (err) throw err;
      onAfter();
    } catch (err) {
      setError(`Rejeitar átomo falhou: ${err.message || err}`);
    } finally { setBusy(false); }
  }, [onAfter, user]);

  const commitAndApproveOp = useCallback(async (op, { silent = false } = {}) => {
    // TB-02: tenta executar a operação no grafo. Se falhar e não for skip,
    // pergunta ao humano se quer aprovar mesmo sem commit (force-approve).
    const result = await commitGraphOperation(op, user?.email || 'unknown');
    let willMarkApproved = result.success;
    if (!result.success && !result.skip) {
      if (silent) {
        // Em batch: skip-on-error (não bloqueia outros ops)
        onCommitFeedback?.({ kind: 'error', op, message: result.error });
        return { skipped: true };
      }
      const force = window.confirm(
        `Erro ao executar a operação:\n\n${result.error}\n\nAprovar mesmo assim (sem commit no grafo)?`
      );
      if (!force) return { aborted: true };
      willMarkApproved = true;
    }

    if (result.skip) {
      onCommitFeedback?.({ kind: 'skip', op, message: result.error });
      // Skip = não marca como approved (fica pendente até a entidade-pai existir)
      return { skipped: true };
    }

    const { error: err } = await supabase
      .from('proposed_graph_operations')
      .update({
        review_status: 'approved',
        reviewed_by: user?.email || 'unknown',
        reviewed_at: new Date().toISOString(),
        applied_id: result.applied_id || null,
        applied_at: result.success ? new Date().toISOString() : null,
      })
      .eq('id', op.id);
    if (err) throw err;

    if (willMarkApproved) {
      onCommitFeedback?.({
        kind: result.success ? 'success' : 'forced',
        op,
        message: result.message || (result.success ? 'Aplicado' : 'Aprovado sem commit'),
      });
    }
    return { ok: true };
  }, [user, onCommitFeedback]);

  const handleApproveOp = useCallback(async (op) => {
    setBusy(true); setError(null);
    try {
      await commitAndApproveOp(op);
      onAfter();
    } catch (err) {
      setError(`Aprovar operação falhou: ${err.message || err}`);
    } finally { setBusy(false); }
  }, [commitAndApproveOp, onAfter]);

  const handleRejectOp = useCallback(async (op) => {
    const reason = window.prompt('Motivo da rejeição (opcional):');
    if (reason === null) return;
    setBusy(true); setError(null);
    try {
      const { error: err } = await supabase
        .from('proposed_graph_operations')
        .update({
          review_status: 'rejected',
          reviewed_by: user?.email || 'unknown',
          reviewed_at: new Date().toISOString(),
          rejection_reason: reason || null,
        })
        .eq('id', op.id);
      if (err) throw err;
      onAfter();
    } catch (err) {
      setError(`Rejeitar operação falhou: ${err.message || err}`);
    } finally { setBusy(false); }
  }, [onAfter, user]);

  const handleAutoApproveEligible = useCallback(async () => {
    if (eligibleAtoms.length === 0) return;
    if (!window.confirm(`Auto-aprovar ${eligibleAtoms.length} átomo(s) low-risk com confiança ≥ 95%?`)) return;
    setBusy(true); setError(null);
    try {
      for (const a of eligibleAtoms) {
        // Sequential — preserva semântica de erro e evita lock contention.
        // eslint-disable-next-line no-await-in-loop
        await approveAtomCommit(a, 'low_risk_literal_extraction_v1');
      }
      onAfter();
    } catch (err) {
      setError(`Auto-aprovar falhou: ${err.message || err}`);
    } finally { setBusy(false); }
  }, [eligibleAtoms, approveAtomCommit, onAfter]);

  // P25 (TB-02.2): "Aprovar todos" pula atoms high-risk — eles ficam na fila
  // para revisão individual. Botão separado handleApproveHighRisk exige
  // confirmação explícita.
  const safeAtoms = useMemo(() => atoms.filter((a) => a.risk_level !== 'high'), [atoms]);
  const highRiskAtoms = useMemo(() => atoms.filter((a) => a.risk_level === 'high'), [atoms]);

  const handleApproveAll = useCallback(async () => {
    const msg = highRiskAtoms.length > 0
      ? `Aprovar ${ops.length} operação${ops.length !== 1 ? 'ões' : ''} + ${safeAtoms.length} fato${safeAtoms.length !== 1 ? 's' : ''} seguros?\n\n${highRiskAtoms.length} fato${highRiskAtoms.length !== 1 ? 's' : ''} HIGH-RISK ficarão na fila para revisão individual.`
      : `Aprovar TUDO neste run (${ops.length} ops + ${atoms.length} fatos)?`;
    if (!window.confirm(msg)) return;
    setBusy(true); setError(null);
    try {
      // Ops: ordena por APPROVE_ORDER (proposal → entity → edge → link)
      // e chama o commit engine. Em batch (silent), erros de uma op não
      // bloqueiam as outras — viram toast no painel "Commits recentes".
      const ordered = [...ops].sort(
        (a, b) => (APPROVE_ORDER[a.operation] ?? 9) - (APPROVE_ORDER[b.operation] ?? 9)
      );
      for (const op of ordered) {
        // eslint-disable-next-line no-await-in-loop
        await commitAndApproveOp(op, { silent: true });
      }
      // Atoms: só low/medium — high-risk continuam pendentes (P25).
      for (const a of safeAtoms) {
        // eslint-disable-next-line no-await-in-loop
        await approveAtomCommit(a, null);
      }
      onAfter();
    } catch (err) {
      setError(`Aprovar todos falhou: ${err.message || err}`);
    } finally { setBusy(false); }
  }, [ops, atoms, safeAtoms, highRiskAtoms, approveAtomCommit, commitAndApproveOp, onAfter]);

  const handleApproveHighRisk = useCallback(async () => {
    if (highRiskAtoms.length === 0) return;
    if (!window.confirm(
      `Aprovar ${highRiskAtoms.length} fato${highRiskAtoms.length !== 1 ? 's' : ''} HIGH-RISK?\n\n`
      + 'Estes fatos foram marcados como críticos (jurídico/financeiro/risco). '
      + 'Confirme apenas após verificar cada um individualmente.'
    )) return;
    setBusy(true); setError(null);
    try {
      for (const a of highRiskAtoms) {
        // eslint-disable-next-line no-await-in-loop
        await approveAtomCommit(a, null);
      }
      onAfter();
    } catch (err) {
      setError(`Aprovar high-risk falhou: ${err.message || err}`);
    } finally { setBusy(false); }
  }, [highRiskAtoms, approveAtomCommit, onAfter]);

  const handleRejectAll = useCallback(async () => {
    const reason = window.prompt(`Rejeitar TODOS (${ops.length + atoms.length}) — motivo (opcional):`);
    if (reason === null) return;
    setBusy(true); setError(null);
    try {
      const stamp = {
        review_status: 'rejected',
        reviewed_by: user?.email || 'unknown',
        reviewed_at: new Date().toISOString(),
        rejection_reason: reason || null,
      };
      if (ops.length > 0) {
        const { error: err } = await supabase
          .from('proposed_graph_operations')
          .update(stamp).in('id', ops.map(o => o.id));
        if (err) throw err;
      }
      if (atoms.length > 0) {
        const { error: err } = await supabase
          .from('proposed_knowledge_atoms')
          .update(stamp).in('id', atoms.map(a => a.id));
        if (err) throw err;
      }
      onAfter();
    } catch (err) {
      setError(`Rejeitar todos falhou: ${err.message || err}`);
    } finally { setBusy(false); }
  }, [ops, atoms, user, onAfter]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
          <FileText size={14} className="text-indigo-600 flex-shrink-0" />
          <span className="text-sm font-semibold text-slate-800 truncate" title={run.file_name || ''}>
            {run.file_name || '(sem nome)'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-shrink-0">
          {run.document_type && (
            <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-700 font-medium">
              {run.document_type}
            </span>
          )}
          <span>{ops.length} op{ops.length !== 1 ? 's' : ''}</span>
          <span>·</span>
          <span>{atoms.length} fato{atoms.length !== 1 ? 's' : ''}</span>
          {eligibleAtoms.length > 0 && (
            <>
              <span>·</span>
              <span className="text-emerald-700">{eligibleAtoms.length} auto-eleg.</span>
            </>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-200">
          {error && (
            <div className="m-3 p-2 bg-red-50 border border-red-200 rounded text-[11px] text-red-800">
              {error}
            </div>
          )}

          {ops.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Operações no grafo
              </div>
              {ops.map(op => (
                <OperationRow
                  key={op.id}
                  op={op}
                  busy={busy}
                  onApprove={handleApproveOp}
                  onReject={handleRejectOp}
                />
              ))}
            </div>
          )}

          {atoms.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Fatos extraídos
              </div>
              {atoms.map(a => (
                <AtomRow
                  key={a.id}
                  atom={a}
                  busy={busy}
                  onApprove={handleApproveAtom}
                  onReject={handleRejectAtom}
                />
              ))}
            </div>
          )}

          <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={handleApproveAll}
              disabled={busy || (ops.length === 0 && atoms.length === 0)}
              className="px-2.5 py-1 text-[11px] bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
              title={highRiskAtoms.length > 0 ? `Pula ${highRiskAtoms.length} fato(s) HIGH-RISK` : ''}
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
              {highRiskAtoms.length > 0
                ? `Aprovar seguros (${ops.length + safeAtoms.length})`
                : 'Aprovar todos'}
            </button>
            {highRiskAtoms.length > 0 && (
              <button
                type="button"
                onClick={handleApproveHighRisk}
                disabled={busy}
                className="px-2.5 py-1 text-[11px] bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                title="Aprovar fatos HIGH-RISK após revisão individual"
              >
                <AlertTriangle size={11} /> Aprovar high-risk ({highRiskAtoms.length})
              </button>
            )}
            <button
              type="button"
              onClick={handleAutoApproveEligible}
              disabled={busy || eligibleAtoms.length === 0}
              className="px-2.5 py-1 text-[11px] bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
            >
              <Zap size={11} /> Auto-aprovar elegíveis ({eligibleAtoms.length})
            </button>
            <button
              type="button"
              onClick={handleRejectAll}
              disabled={busy || (ops.length === 0 && atoms.length === 0)}
              className="px-2.5 py-1 text-[11px] bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50 flex items-center gap-1"
            >
              <XCircle size={11} /> Rejeitar todos
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Hook utilitário pro badge de pendentes na nav ──────────── */

export const usePendingIntakeCount = () => {
  const [count, setCount] = useState(null);

  const fetchCount = useCallback(async () => {
    try {
      const [{ count: ops }, { count: atoms }] = await Promise.all([
        supabase
          .from('proposed_graph_operations')
          .select('id', { count: 'exact', head: true })
          .eq('review_status', 'pending'),
        supabase
          .from('proposed_knowledge_atoms')
          .select('id', { count: 'exact', head: true })
          .eq('review_status', 'proposed'),
      ]);
      setCount((ops || 0) + (atoms || 0));
    } catch (err) {
      console.error('[usePendingIntakeCount]', err);
      setCount(null);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 60_000); // refresh leve a cada minuto
    return () => clearInterval(id);
  }, [fetchCount]);

  return { pendingCount: count, refetch: fetchCount };
};

/* ── Componente principal ───────────────────────────────────── */

const IntakeReviewQueue = () => {
  const [ops, setOps] = useState([]);
  const [atoms, setAtoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoProposedMsg, setAutoProposedMsg] = useState(null);
  const [autoProposeIssue, setAutoProposeIssue] = useState(null);
  const autoProposingRef = useRef(false);
  const [commitFeedback, setCommitFeedback] = useState([]); // {kind, message, ts}

  const pushCommitFeedback = useCallback(({ kind, op, message }) => {
    const label = op?.operation && op?.target_table ? `${op.operation} → ${op.target_table}` : '';
    const entry = { kind, message: label ? `${label}: ${message}` : message, ts: Date.now() };
    setCommitFeedback((prev) => [...prev.slice(-9), entry]);
    if (kind === 'success' || kind === 'forced') {
      setTimeout(() => {
        setCommitFeedback((prev) => prev.filter((e) => e.ts !== entry.ts));
      }, 5000);
    }
  }, []);

  const dismissCommitFeedback = useCallback(() => setCommitFeedback([]), []);

  const fetchOpsAndAtoms = useCallback(async () => {
    const [opsRes, atomsRes] = await Promise.all([
      supabase
        .from('proposed_graph_operations')
        .select('*, intake_runs(file_name, document_type, pipeline_status, attachment_id)')
        .eq('review_status', 'pending')
        .order('created_at', { ascending: true }),
      supabase
        .from('proposed_knowledge_atoms')
        .select('*, intake_runs(file_name, document_type, pipeline_status, attachment_id)')
        .eq('review_status', 'proposed')
        .order('created_at', { ascending: true }),
    ]);
    if (opsRes.error) throw opsRes.error;
    if (atomsRes.error) throw atomsRes.error;
    return { ops: opsRes.data || [], atoms: atomsRes.data || [] };
  }, []);

  // Roda o bridge silencioso para runs com extracted_json e sem ops propostas.
  // Idempotente: se um run já foi bridged em outra sessão, é skip silencioso.
  const autoProposeUnbridged = useCallback(async (existingRunIds) => {
    if (autoProposingRef.current) return { ranOps: 0, ranAtoms: 0, errors: [] };
    autoProposingRef.current = true;
    try {
      const { data: candidates, error: qErr } = await supabase
        .from('intake_runs')
        .select('id, file_name, document_type, extracted_json, attachment_id, pipeline_status')
        .not('extracted_json', 'is', null)
        .order('created_at', { ascending: false })
        .limit(30);
      if (qErr) throw qErr;

      const needsBridge = (candidates || []).filter((ir) => {
        if (!ir.extracted_json || typeof ir.extracted_json !== 'object') return false;
        const inner = ir.extracted_json.fields && typeof ir.extracted_json.fields === 'object'
          ? ir.extracted_json.fields
          : ir.extracted_json;
        if (Object.keys(inner).length === 0) return false;
        return !existingRunIds.has(ir.id);
      });

      if (needsBridge.length === 0) return { ranOps: 0, ranAtoms: 0, ranAutoApproved: 0, errors: [] };

      let totalOps = 0;
      let totalAtoms = 0;
      let totalAuto = 0;
      const errors = [];
      for (const ir of needsBridge) {
        // eslint-disable-next-line no-await-in-loop
        const r = await proposeFromExtraction(ir);
        totalOps += r.opsCreated;
        totalAtoms += r.atomsCreated;
        totalAuto += r.atomsAutoApproved || 0;
        if (r.errors && r.errors.length > 0) errors.push(...r.errors);
      }
      return { ranOps: totalOps, ranAtoms: totalAtoms, ranAutoApproved: totalAuto, errors, runsTouched: needsBridge.length };
    } finally {
      autoProposingRef.current = false;
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAutoProposeIssue(null);
    try {
      const first = await fetchOpsAndAtoms();
      const existingRunIds = new Set([
        ...first.ops.map((o) => o.intake_run_id),
        ...first.atoms.map((a) => a.intake_run_id),
      ]);

      const auto = await autoProposeUnbridged(existingRunIds);

      let finalData = first;
      if (auto.ranOps + auto.ranAtoms > 0) {
        finalData = await fetchOpsAndAtoms();
        const autoTxt = auto.ranAutoApproved > 0
          ? ` · ${auto.ranAutoApproved} fato${auto.ranAutoApproved !== 1 ? 's' : ''} auto-aprovado${auto.ranAutoApproved !== 1 ? 's' : ''} (confiança ≥ 85%)`
          : '';
        setAutoProposedMsg(
          `${auto.ranOps} operação${auto.ranOps !== 1 ? 'ões' : ''} + ${auto.ranAtoms} fato${auto.ranAtoms !== 1 ? 's' : ''} `
          + `propost${auto.ranAtoms !== 1 ? 'os' : 'o'} automaticamente de ${auto.runsTouched} documento${auto.runsTouched !== 1 ? 's' : ''}${autoTxt}`
        );
        setTimeout(() => setAutoProposedMsg(null), 6000);
      }
      if (auto.errors && auto.errors.length > 0) {
        setAutoProposeIssue(`${auto.errors.length} documento${auto.errors.length !== 1 ? 's' : ''} com problema no bridge — veja console`);
      }

      setOps(finalData.ops);
      setAtoms(finalData.atoms);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [fetchOpsAndAtoms, autoProposeUnbridged]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Agrupa por intake_run_id
  const byRun = useMemo(() => {
    const map = new Map();
    const ensure = (id, runMeta) => {
      if (!map.has(id)) map.set(id, { runId: id, run: runMeta || {}, ops: [], atoms: [] });
      return map.get(id);
    };
    for (const o of ops) {
      const slot = ensure(o.intake_run_id, o.intake_runs);
      slot.ops.push(o);
    }
    for (const a of atoms) {
      const slot = ensure(a.intake_run_id, a.intake_runs);
      slot.atoms.push(a);
    }
    return Array.from(map.values());
  }, [ops, atoms]);

  const total = ops.length + atoms.length;
  const expandAll = byRun.length <= 3;

  return (
    <div className="space-y-3">
      {FEATURE_FLAGS.INTAKE_V2_LAND_TITLE && <IntakeFactsV2Section />}
    <section className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-800">Fila de revisão</h2>
          <span className="text-[10px] font-medium text-slate-500 bg-white border border-slate-200 px-1.5 py-0.5 rounded">
            {loading ? '…' : total}
          </span>
        </div>
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          className="text-[11px] px-2 py-1 bg-white border border-slate-200 rounded text-slate-700 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1"
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          Atualizar
        </button>
      </div>

      {commitFeedback.length > 0 && (
        <div className="mb-2 bg-white border border-slate-200 rounded overflow-hidden">
          <div className="flex items-center justify-between px-2 py-1 bg-slate-50 border-b border-slate-200">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Commits recentes
            </span>
            <button
              type="button"
              onClick={dismissCommitFeedback}
              className="text-[10px] text-slate-500 hover:text-slate-800"
            >
              Limpar
            </button>
          </div>
          <ul className="max-h-32 overflow-y-auto">
            {commitFeedback.map((e) => {
              const tone = e.kind === 'success' ? 'text-emerald-700'
                : e.kind === 'forced' ? 'text-amber-700'
                  : e.kind === 'skip' ? 'text-slate-500'
                    : 'text-red-700';
              const icon = e.kind === 'success' ? '✅'
                : e.kind === 'forced' ? '⚠️'
                  : e.kind === 'skip' ? '⏭'
                    : '❌';
              return (
                <li key={e.ts} className={`px-2 py-1 text-[11px] border-b border-slate-100 last:border-b-0 ${tone}`}>
                  <span className="mr-1">{icon}</span>{e.message}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {autoProposedMsg && (
        <button
          type="button"
          onClick={() => setAutoProposedMsg(null)}
          className="w-full mb-2 px-2 py-1.5 bg-indigo-50 border border-indigo-200 rounded text-[11px] text-indigo-800 flex items-center gap-1.5 hover:bg-indigo-100"
          title="Fechar"
        >
          <Zap size={11} className="text-indigo-600" />
          <span>{autoProposedMsg}</span>
        </button>
      )}

      {autoProposeIssue && (
        <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800 flex items-center gap-1.5">
          <AlertTriangle size={11} /> {autoProposeIssue}
        </div>
      )}

      {error && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-[11px] text-red-800">
          {error}
        </div>
      )}

      {!loading && total === 0 && (
        <div className="bg-white border border-slate-200 rounded p-6 text-center text-xs text-slate-500">
          Nenhuma operação pendente de revisão. 🎉
        </div>
      )}

      {!loading && total > 0 && (
        <div className="space-y-2">
          {byRun.map(({ runId, run, ops: rOps, atoms: rAtoms }) => (
            <RunCard
              key={runId}
              run={run}
              ops={rOps}
              atoms={rAtoms}
              defaultExpanded={expandAll}
              onAfter={fetchAll}
              onCommitFeedback={pushCommitFeedback}
            />
          ))}
        </div>
      )}
    </section>
    </div>
  );
};

export default IntakeReviewQueue;
